import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Crown, Zap, ArrowUpRight, AlertTriangle, CalendarClock, Activity, Clock
} from 'lucide-react';

const PLAN_NAMES = {
  annual: 'Plano Anual',
  biennial: 'Plano 24 Meses',
  basic: 'Vital Fit',
  plus: 'Vital Plus',
  premium: 'Vital Premium',
  gold: 'Vital Gold',
  platinum: 'Vital Platinum',
  corporate: 'Vital Corporativo',
  professional: 'Vital Profissional',
  essential: 'Vital Essencial',
};

const computeExpiration = (contractStart, contractMonths) => {
  if (!contractStart || !contractMonths) return null;
  const start = new Date(contractStart);
  const end = new Date(start);
  end.setMonth(end.getMonth() + contractMonths);
  const now = new Date();
  const totalMs = end.getTime() - start.getTime();
  const elapsedMs = now.getTime() - start.getTime();
  const remainingMs = end.getTime() - now.getTime();
  const daysRemaining = Math.ceil(remainingMs / 86400000);
  const percentUsed = Math.min(Math.max((elapsedMs / totalMs) * 100, 0), 100);
  const expired = now > end;
  return { start, end, daysRemaining, percentUsed, expired };
};

const computeMonthlyExpiration = (planStartDate) => {
  if (!planStartDate) return null;
  const start = new Date(planStartDate);
  const end = new Date(start);
  end.setMonth(end.getMonth() + 1);
  const now = new Date();
  const totalMs = end.getTime() - start.getTime();
  const elapsedMs = now.getTime() - start.getTime();
  const remainingMs = end.getTime() - now.getTime();
  const daysRemaining = Math.ceil(remainingMs / 86400000);
  const percentUsed = Math.min(Math.max((elapsedMs / totalMs) * 100, 0), 100);
  const expired = now > end;
  return { start, end, daysRemaining, percentUsed, expired };
};

const formatDate = (date) => {
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PlanBanner = ({ planData, variant = 'company', className = '' }) => {
  const navigate = useNavigate();

  if (!planData) return null;

  const plan = planData.plan;
  const planName = PLAN_NAMES[plan] || plan || 'Sem plano ativo';
  const hasPlan = !!plan;

  let expInfo = null;
  if (variant === 'student') {
    expInfo = computeMonthlyExpiration(planData.plan_start_date);
  } else {
    expInfo = computeExpiration(planData.contract_start, planData.contract_months);
  }

  const isExpired = expInfo?.expired;
  const isExpiringSoon = expInfo && !isExpired && expInfo.daysRemaining <= 30;

  const plansRoute = variant === 'company' ? '/plans/company'
    : variant === 'gym' ? '/plans/gym'
    : '/plans/student';

  const getTheme = () => {
    if (!hasPlan || isExpired) return { icon: 'bg-slate-100 text-slate-400', accent: 'slate', btn: 'bg-blue-600 hover:bg-blue-700' };
    if (isExpiringSoon) return { icon: 'bg-amber-100 text-amber-600', accent: 'amber', btn: 'bg-amber-600 hover:bg-amber-700' };
    if (variant === 'gym') return { icon: 'bg-orange-100 text-orange-500', accent: 'orange', btn: 'bg-slate-900 hover:bg-slate-800' };
    if (variant === 'student') {
      if (['gold', 'platinum'].includes(plan)) return { icon: 'bg-amber-100 text-amber-600', accent: 'amber', btn: 'bg-slate-900 hover:bg-slate-800' };
      if (plan === 'premium') return { icon: 'bg-violet-100 text-violet-600', accent: 'violet', btn: 'bg-slate-900 hover:bg-slate-800' };
      return { icon: 'bg-blue-100 text-blue-600', accent: 'blue', btn: 'bg-blue-600 hover:bg-blue-700' };
    }
    return { icon: 'bg-blue-100 text-blue-600', accent: 'blue', btn: 'bg-slate-900 hover:bg-slate-800' };
  };

  const theme = getTheme();

  const getStatusBadge = () => {
    if (!hasPlan) return null;
    if (isExpired) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expirado</span>;
    if (isExpiringSoon) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Expira em breve</span>;
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ativo</span>;
  };

  const getButtonLabel = () => {
    if (!hasPlan) return 'Escolher Plano';
    if (isExpired) return 'Renovar Plano';
    if (isExpiringSoon) return 'Renovar Plano';
    return 'Upgrade / Renovar';
  };

  const getIcon = () => {
    if (isExpired || !hasPlan) return <AlertTriangle className="w-6 h-6" />;
    if (['gold', 'platinum', 'corporate'].includes(plan)) return <Crown className="w-6 h-6" />;
    return <Zap className="w-6 h-6" />;
  };

  return (
    <div
      data-testid="plan-banner"
      className={`relative overflow-hidden rounded-2xl border ${
        isExpired ? 'border-red-200 bg-red-50/30' :
        isExpiringSoon ? 'border-amber-200 bg-amber-50/30' :
        'border-slate-200 bg-white'
      } ${className}`}
    >
      <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 md:p-6">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${theme.icon}`}>
            {getIcon()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-slate-900 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {planName}
              </h3>
              {getStatusBadge()}
            </div>

            {/* Expiration info */}
            {hasPlan && expInfo && (
              <div className="mt-1.5 space-y-1.5">
                <div className="flex items-center gap-3 text-sm text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5" />
                    {isExpired
                      ? `Expirou em ${formatDate(expInfo.end)}`
                      : `Expira em ${formatDate(expInfo.end)}`
                    }
                  </span>
                  {!isExpired && (
                    <span className={`flex items-center gap-1 font-medium ${
                      isExpiringSoon ? 'text-amber-600' : 'text-slate-600'
                    }`}>
                      <Clock className="w-3.5 h-3.5" />
                      {expInfo.daysRemaining} {expInfo.daysRemaining === 1 ? 'dia restante' : 'dias restantes'}
                    </span>
                  )}
                </div>
                <div className="w-full max-w-xs">
                  <Progress
                    value={expInfo.percentUsed}
                    className={`h-1.5 ${
                      isExpired ? '[&>div]:bg-red-500' :
                      isExpiringSoon ? '[&>div]:bg-amber-500' :
                      '[&>div]:bg-emerald-500'
                    } bg-slate-100`}
                  />
                </div>
              </div>
            )}

            {!hasPlan && (
              <p className="text-sm text-slate-500 mt-0.5">Selecione um plano para começar</p>
            )}
          </div>
        </div>

        <Button
          onClick={() => navigate(plansRoute)}
          data-testid="plan-upgrade-btn"
          className={`gap-2 ${theme.btn} text-white flex-shrink-0`}
        >
          {getButtonLabel()}
          <ArrowUpRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default PlanBanner;
